import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import {
  BokkzAlgDataService,
  BokkzJrDataService, BokkzJrLogic, EigenVermogenNatuurlijkPersoon,
  EigenVermogenNatuurlijkPersoonMutatieoverzicht, Jaarrapportage, JaarrapportageEntity, Jaarrekening, ZakelijkePartner,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {SharedUiDetailsModule} from "@snelstart/ui-details";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {CommonModule} from "@angular/common";
import {Subject, takeUntil} from "rxjs";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-zakelijke-partners',
  templateUrl: './zakelijke-partners.component.html',
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    CommonModule
  ],
  styleUrls: ['./zakelijke-partners.component.css']
})
export class ZakelijkePartnersComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private destroy$ = new Subject<void>();
  public jrEntity = {} as JaarrapportageEntity;
  public zakelijkePartnersFg = {} as FormGroup;
  public focusedElementId: string | null = null;
  public index = 0;
  public isEenmanszaak = false;
  public deleteDialogVisible = false;
  public indexPendingDelete: number = -1;

  constructor(
    public algDataService: BokkzAlgDataService,
    public jrDataService: BokkzJrDataService,
    public jrLogic: BokkzJrLogic,
    private fb: FormBuilder) {
    this.zakelijkePartnersFg = this.fb.group({
      zakelijkePartners: this.fb.array<ZakelijkePartner>([]),
    })
  }

  get zakelijkePartners(): FormArray {
    return this.zakelijkePartnersFg.get('zakelijkePartners') as FormArray;
  }

  get displayNameZakelijkPartnerPendingDelete(): string {
    return this.displayNameZakelijkPartner(this.indexPendingDelete);
  }

  displayNameZakelijkPartner(indexZp: number) {
    const item = this.zakelijkePartners.at(indexZp);

    if (!item) {
      return `Zakelijke partner ${indexZp + 1}`;
    }

    const zakelijkePartner = item.value as ZakelijkePartner;

    return this.jrDataService.getDisplayNameZakelijkPartner(zakelijkePartner, indexZp)
  }

  addRow(): void {
    this.zakelijkePartners.push(this.createFormGroupZakelijkePartnerEnEigenVermogenMutatieoverzicht());
  }

  onDelete(index: number): void {
    this.indexPendingDelete = index;
    this.deleteDialogVisible = true;
  }

  closeDeleteDialog() {
    this.deleteDialogVisible = false;
    setTimeout(() => {
      this.indexPendingDelete = -1;
    }, 250)
  }

  cancelDelete() {
    this.closeDeleteDialog();
  }

  confirmDelete(): void {
    if (this.indexPendingDelete < 0) {
      this.closeDeleteDialog();
      return;
    }

    this.zakelijkePartners.removeAt(this.indexPendingDelete);
    this.saveForm();
    this.closeDeleteDialog();
  }

  ngOnInit(): void {
    this.jrDataService.jaarrapportageEntity.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.jrEntity = value;
        const item = value.rapportage?.zakelijkePartners;
        if (item) {
          this.updateForm();
        }
      })
    this.jrDataService.isEenmanszaak
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.isEenmanszaak = value;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createFormGroupZakelijkePartnerEnEigenVermogenMutatieoverzicht(zakelijkePartner?: ZakelijkePartner, eigenVermogenNatuurlijkPersoon?: EigenVermogenNatuurlijkPersoon): FormGroup {

    const fgZakelijkePartnerEnEigenVermogenMutatieoverzicht = this.jrDataService.createFormGroupZakelijkePartner(zakelijkePartner);

    const fgEigenVermogenNatuurlijkPersoonMutatieoverzicht = this.fb.group({
      interestOnCapitalPercentage: this.fb.group({
        valuePeriod1: eigenVermogenNatuurlijkPersoon?.eigenVermogenNatuurlijkPersoonMutatieoverzicht?.interestOnCapitalPercentage?.valuePeriod1 || null as unknown as number,
        valuePeriod2: eigenVermogenNatuurlijkPersoon?.eigenVermogenNatuurlijkPersoonMutatieoverzicht?.interestOnCapitalPercentage?.valuePeriod2 || null as unknown as number,
      }),
      remunerationAllowance: this.fb.group({
        valuePeriod1: eigenVermogenNatuurlijkPersoon?.eigenVermogenNatuurlijkPersoonMutatieoverzicht?.remunerationAllowance?.valuePeriod1 || null as unknown as number,
        valuePeriod2: eigenVermogenNatuurlijkPersoon?.eigenVermogenNatuurlijkPersoonMutatieoverzicht?.remunerationAllowance?.valuePeriod2 || null as unknown as number,
      }),
      shareInResultPercentage: this.fb.group({
        valuePeriod1: eigenVermogenNatuurlijkPersoon?.eigenVermogenNatuurlijkPersoonMutatieoverzicht?.shareInResultPercentage?.valuePeriod1 || (this.isEenmanszaak ? 100 : null as unknown as number),
        valuePeriod2: eigenVermogenNatuurlijkPersoon?.eigenVermogenNatuurlijkPersoonMutatieoverzicht?.shareInResultPercentage?.valuePeriod2 || null as unknown as number,
      })
    });

    fgZakelijkePartnerEnEigenVermogenMutatieoverzicht.addControl('eigenVermogenNatuurlijkPersoonMutatieoverzicht', fgEigenVermogenNatuurlijkPersoonMutatieoverzicht);

    return fgZakelijkePartnerEnEigenVermogenMutatieoverzicht;
  }

  private updateForm(): void {
    const lijstZakelijkePartners = this.jrEntity.rapportage?.zakelijkePartners || [];
    const lijstEigenVermogenNatuurlijkePersonenSpecificatie = this.jrEntity.rapportage?.jaarrekeningen?.at(0)?.balans?.eigenVermogenNatuurlijkePersonen?.eigenVermogenNatuurlijkePersonenSpecificatie || [];
    const formArray = this.zakelijkePartnersFg.get('zakelijkePartners') as FormArray;
    const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;
    while (formArray.length > lijstZakelijkePartners.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijstZakelijkePartners.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        this.patchDataZakelijkePartnerEnEigenVermogenNatuurlijkPersoon(control, lijstZakelijkePartners[i], lijstEigenVermogenNatuurlijkePersonenSpecificatie[i]);
      } else {
        const formGroup = this.createFormGroupZakelijkePartnerEnEigenVermogenMutatieoverzicht(lijstZakelijkePartners[i], lijstEigenVermogenNatuurlijkePersonenSpecificatie[i])
        formArray.push(formGroup, {emitEvent: false});
      }
    }
    if (focusedControl && document.contains(focusedControl)) {
      focusedControl.focus();
    }
  }

  private patchDataZakelijkePartnerEnEigenVermogenNatuurlijkPersoon(control: FormGroup, zakelijkePartner?: ZakelijkePartner, eigenVermogenNatuurlijkPersoon?: EigenVermogenNatuurlijkPersoon) {

    //TODO tijdelijke fix - controleren op !dirty i.v.m. overschrijven waarden met bij snelle invoer
    if (zakelijkePartner && !control?.dirty) {
      control.patchValue(zakelijkePartner, {emitEvent: false});
    }

    if (eigenVermogenNatuurlijkPersoon && !control?.dirty) {
      const mutatieoverzicht = eigenVermogenNatuurlijkPersoon.eigenVermogenNatuurlijkPersoonMutatieoverzicht;
      if (mutatieoverzicht) {
        control.patchValue({eigenVermogenNatuurlijkPersoonMutatieoverzicht: mutatieoverzicht}, {emitEvent: false});
      }
    }
  }

  public saveForm(skipCalculation?: Boolean): void {
    this.zakelijkePartnersFg.updateValueAndValidity({ emitEvent: false });
    setTimeout(() => {

      let eigenVermogenNatuurlijkePersonen = this.jrEntity?.rapportage?.jaarrekeningen?.at(0)?.balans?.eigenVermogenNatuurlijkePersonen;

      if (eigenVermogenNatuurlijkePersonen?.eigenVermogenNatuurlijkePersonenSpecificatie && this.indexPendingDelete > -1) {
        const eigenVermogenNatuurlijkPersoonTeVerwijderen = eigenVermogenNatuurlijkePersonen.eigenVermogenNatuurlijkePersonenSpecificatie.at(this.indexPendingDelete);
        eigenVermogenNatuurlijkePersonen.eigenVermogenNatuurlijkePersonenSpecificatie = eigenVermogenNatuurlijkePersonen.eigenVermogenNatuurlijkePersonenSpecificatie.filter(evnp => evnp !== eigenVermogenNatuurlijkPersoonTeVerwijderen);
      }

      let eigenVermogenNatuurlijkePersonenSpecificatie = eigenVermogenNatuurlijkePersonen?.eigenVermogenNatuurlijkePersonenSpecificatie || [];
      const zakelijkePartnersArray = this.zakelijkePartnersFg.getRawValue().zakelijkePartners;

      for (let i = 0; i < zakelijkePartnersArray.length; i++) {
        eigenVermogenNatuurlijkePersonenSpecificatie[i] = {
          ...eigenVermogenNatuurlijkePersonenSpecificatie[i],
          identificationNumberTypedMember: zakelijkePartnersArray[i].identificationNumberTypedMember,
          eigenVermogenNatuurlijkPersoonMutatieoverzicht: {
            ...eigenVermogenNatuurlijkePersonenSpecificatie[i]?.eigenVermogenNatuurlijkPersoonMutatieoverzicht,
            shareInResultPercentage: zakelijkePartnersArray[i].eigenVermogenNatuurlijkPersoonMutatieoverzicht.shareInResultPercentage,
            remunerationAllowance: zakelijkePartnersArray[i].eigenVermogenNatuurlijkPersoonMutatieoverzicht.remunerationAllowance,
            interestOnCapitalPercentage: zakelijkePartnersArray[i].eigenVermogenNatuurlijkPersoonMutatieoverzicht.interestOnCapitalPercentage,
          },
        } as EigenVermogenNatuurlijkPersoon;
      }

      this.jrEntity = {
        ...this.jrEntity,
        rapportage: {
          ...this.jrEntity.rapportage,
          zakelijkePartners: this.zakelijkePartnersFg.getRawValue().zakelijkePartners,
        }
      } as Jaarrapportage

      if (this.jrEntity && this.jrEntity.rapportage && this.jrEntity.rapportage.jaarrekeningen) {
        this.jrEntity.rapportage.jaarrekeningen[0] = {
          ...this.jrEntity.rapportage.jaarrekeningen[0],
          balans: {
            ...this.jrEntity.rapportage.jaarrekeningen[0].balans,
            eigenVermogenNatuurlijkePersonen: {
              ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.eigenVermogenNatuurlijkePersonen,
              eigenVermogenNatuurlijkePersonenSpecificatie: eigenVermogenNatuurlijkePersonenSpecificatie,
            },
          },
        } as Jaarrekening;
      }

      this.jrDataService.updateEntity(this.jrEntity);
      if (skipCalculation !== true) {
        this.jrLogic.getCalculation(this.jrEntity);
      }
    }, 0)
  }
}
